import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { clarifyQuestionKey, resultIntroKey } from "./learning-chat-flow.js";
import {
  THINKING_MS,
  createSessionId,
  focusGreetingSession,
  replyToGreeting,
  todayDateKey,
  type ChatSession,
} from "./learning-chat-mock.js";
import type { ChatSituationId } from "./learning-chat-situations.js";

export function useLearningChatPage() {
  const { t, i18n } = useTranslation();
  const demoPrompt = t("learningRoute.chatDemoPrompt");
  const [searchParams, setSearchParams] = useSearchParams();
  const focusTopic = searchParams.get("focusTopic");
  const startedFocusTopic = useRef(focusTopic);
  const greetingText = (topic: string) =>
    `${t("learningRoute.chatGreetingLead")} ${topic}${t("learningRoute.chatGreetingTail")}`;
  const [draft, setDraft] = useState(() => (focusTopic ? "" : demoPrompt));
  const [history, setHistory] = useState<ChatSession[]>([]);
  const [active, setActive] = useState<ChatSession | null>(() =>
    focusTopic ? focusGreetingSession(focusTopic, greetingText(focusTopic)) : null,
  );
  const [pendingAdvance, setPendingAdvance] = useState<"clarify" | "result" | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) setDraft(t("learningRoute.chatDemoPrompt"));
  }, [active, i18n.language, t]);

  useEffect(() => {
    if (!focusTopic || startedFocusTopic.current === focusTopic) {
      if (focusTopic) setSearchParams({}, { replace: true });
      return;
    }
    startedFocusTopic.current = focusTopic;
    setDraft("");
    setPendingAdvance(null);
    setActive(focusGreetingSession(focusTopic, greetingText(focusTopic)));
    setSearchParams({}, { replace: true });
  }, [focusTopic, setSearchParams, t]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [active?.messages, active?.phase]);

  useEffect(() => {
    if (!active || active.phase !== "thinking" || !pendingAdvance) return;
    const timer = window.setTimeout(() => {
      setActive((current) => {
        if (!current) return current;
        if (pendingAdvance === "clarify") {
          const question = t(clarifyQuestionKey(current.situationId, current.clarifyStep), {
            question: current.title,
          });
          return {
            ...current,
            phase: "clarify",
            messages: [
              ...current.messages,
              { id: `assistant-${Date.now()}`, kind: "assistant", text: question },
            ],
          };
        }
        return {
          ...current,
          phase: "result",
          messages: [
            ...current.messages,
            {
              id: `assistant-${Date.now()}`,
              kind: "assistant",
              text: t(resultIntroKey(current.situationId)),
            },
          ],
        };
      });
      setPendingAdvance(null);
    }, THINKING_MS);
    return () => window.clearTimeout(timer);
  }, [active, pendingAdvance, t]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language === "en" ? "en-GB" : "nl-NL", {
        day: "numeric",
        month: "short",
      }),
    [i18n.language],
  );

  const startSession = (text: string, situationId?: ChatSituationId) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setDraft("");
    setActive({
      id: createSessionId(),
      title: trimmed,
      dateKey: todayDateKey(),
      clarifyStep: 0,
      phase: "thinking",
      situationId,
      messages: [
        { id: `user-${Date.now()}`, kind: "user", text: trimmed },
        {
          id: `status-${Date.now()}`,
          kind: "status",
          seconds: 12,
          stepKey: "learningRoute.chatThinkingSteps.understand",
        },
      ],
    });
    setPendingAdvance("clarify");
  };

  const answerClarify = (text: string) => {
    if (!active || active.phase !== "clarify") return;
    const fromStep = active.clarifyStep;
    setDraft("");
    setActive({
      ...active,
      clarifyStep: fromStep === 0 ? 1 : fromStep,
      phase: "thinking",
      messages: [
        ...active.messages,
        { id: `user-${Date.now()}`, kind: "user", text },
        {
          id: `status-${Date.now()}`,
          kind: "status",
          seconds: fromStep === 0 ? 3 : 7,
          stepKey: "learningRoute.chatThinkingSteps.organise",
        },
      ],
    });
    setPendingAdvance(fromStep === 0 ? "clarify" : "result");
  };

  const sendFollowUp = (text: string) => {
    if (!active || active.phase !== "result") return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setDraft("");
    setActive({
      ...active,
      messages: [
        ...active.messages,
        { id: `user-${Date.now()}`, kind: "user", text: trimmed },
        { id: `assistant-${Date.now()}`, kind: "assistant", text: t("learningRoute.chatMockReply") },
      ],
    });
  };

  const continueFromGreeting = (text: string) => {
    if (!active || active.phase !== "greeting") return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setDraft("");
    setActive(replyToGreeting(active, trimmed));
    setPendingAdvance("clarify");
  };

  const sendThreadMessage = (text: string) => {
    if (!active) return;
    if (active.phase === "greeting") {
      continueFromGreeting(text);
      return;
    }
    if (active.phase === "clarify") {
      answerClarify(text);
      return;
    }
    if (active.phase === "result") sendFollowUp(text);
  };

  const finalizeForHistory = (session: ChatSession): ChatSession => {
    if (session.phase !== "thinking") return session;
    // Don't persist a mid-think state: history reopen would otherwise hang forever.
    if (pendingAdvance === "result") {
      return {
        ...session,
        phase: "result",
        messages: [
          ...session.messages,
          {
            id: `assistant-${Date.now()}`,
            kind: "assistant",
            text: t(resultIntroKey(session.situationId)),
          },
        ],
      };
    }
    const question = t(clarifyQuestionKey(session.situationId, session.clarifyStep), {
      question: session.title,
    });
    const alreadyAsked = session.messages.some(
      (message) => message.kind === "assistant" && message.text === question,
    );
    return {
      ...session,
      phase: "clarify",
      messages: alreadyAsked
        ? session.messages
        : [...session.messages, { id: `assistant-${Date.now()}`, kind: "assistant", text: question }],
    };
  };

  const goHome = () => {
    if (active) {
      const saved = finalizeForHistory(active);
      setHistory((current) => [saved, ...current.filter((entry) => entry.id !== saved.id)]);
    }
    setActive(null);
    setPendingAdvance(null);
  };

  const openSituation = (id: ChatSituationId) => {
    startSession(t(`learningRoute.situations.${id}.prompt`), id);
  };

  return {
    active,
    bottomRef,
    demoPrompt,
    draft,
    formatDate: (dateKey: string) => dateFormatter.format(new Date(dateKey)),
    goHome,
    history,
    openHistory: (session: ChatSession) => {
      setActive(session);
      setPendingAdvance(null);
    },
    openSituation,
    prefillDemo: () => setDraft(demoPrompt),
    sendDraft: () => startSession(draft),
    sendThreadMessage,
    setDraft,
  };
}
